import { Button } from '@dtx-company/design-system/src/components/Button/Button'
import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material'
import { DEFAULT_FLOWCODE_NICKNAME } from '../../../constants/flowcode'
import { DownloadTemplateModal } from './DownloadTemplateModal'
import { EmailTemplateModal } from '@app/code/src/components/modals/email-code/email-code-modal'
import { FC, useRef } from 'react'
import { Grid } from '@dtx-company/design-system/src/components/Grid/Grid'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { TemplatesFlowcodeBtn } from '../TemplatesFlowcodeBtn/TemplatesFlowcodeBtn'
import { getFlowcodeGeneratorURL } from './EditTemplatePageSecondaryNav.hooks'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useEmailTemplate } from './DownloadTemplateModal.hooks'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import printStoreEvents from '@dtx-company/inter-app/src/event-tracking/events/printStore'
import templatesStoreEvents, {
  events
} from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'

interface EditTemplatePageSecondaryNavProps {
  clearCode: () => void
  backButtonHandler: () => void
}

export const EditTemplatePageSecondaryNav: FC<EditTemplatePageSecondaryNavProps> = ({
  clearCode,
  backButtonHandler
}): JSX.Element => {
  const { setOpen: setSelectFlowcodeModalOpen } = useModal(ModalTypes.SELECT_FLOWCODE_MODAL)
  const {
    modalOpen: isDownloadTemplateModalOpen,
    setOpen: setDownloadTemplateModalOpen,
    setClose: setDownloadTemplateModalClose
  } = useModal(ModalTypes.TEMPLATES_DOWNLOAD_MODAL)
  const { modalOpen: isEmailTemplateModalOpen, setClose: setEmailTemplateModalClose } = useModal(
    ModalTypes.EMAIL_PLACEMENT_TEMPLATE_MODAL
  )

  const { isMobile } = useScreenSizeBreakpoints()
  const { selectedFlowcode, productId, templateId } = useCodeSelector(state => state.printStore)

  const selectedCodeId = selectedFlowcode?.id
  const displaySelectedCodeName = selectedCodeId
    ? selectedFlowcode?.nickname || DEFAULT_FLOWCODE_NICKNAME
    : ''
  const downloadButtonRef = useRef<HTMLButtonElement>(null)

  const onSelectFlowcodeClick = (): void => {
    setSelectFlowcodeModalOpen()
    templatesStoreEvents.Templates_Clicked_SelectFlowcodeButton()
  }

  const onDownloadButtonClick = (): void => {
    setDownloadTemplateModalOpen()
    printStoreEvents.PrintTemplates_TemplateEditor_clicked_downloadbutton()
  }

  const onClickCreateFlowcode = (): void => {
    events.Store_Templates_TemplateEditor_Clicked_Create_Flowcode_Btn({
      templateId,
      productId
    })
  }
  const { onEmailClick } = useEmailTemplate()

  return (
    <>
      <Grid
        container
        columns={isMobile ? 2 : 3}
        sx={{
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
          paddingX: { xs: 3, md: 6 },
          paddingY: 2
        }}
        data-testid="template-secondary-nav-container"
      >
        <Grid xs={1}>
          <Button variant="text" startIcon={ChevronLeftIcon} onClick={backButtonHandler}>
            Exit editor
          </Button>
        </Grid>

        <Grid xs={1} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
          <TemplatesFlowcodeBtn
            hasSelectedFlowcode={Boolean(selectedFlowcode)}
            displayValue={displaySelectedCodeName}
            onFlowcodeRemoved={clearCode}
            onClickCreateFlowcode={onClickCreateFlowcode}
            onClickSelectFlowcode={onSelectFlowcodeClick}
            variant="sm"
          />
        </Grid>

        <Grid xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            data-testid="template-secondary-nav-download-button"
            onClick={onDownloadButtonClick}
            ref={downloadButtonRef}
            disabled={!selectedCodeId}
          >
            Download
          </Button>
        </Grid>
      </Grid>

      {isDownloadTemplateModalOpen && (
        <DownloadTemplateModal
          isOpen={isDownloadTemplateModalOpen}
          onClose={() => setDownloadTemplateModalClose()}
          anchorRef={downloadButtonRef}
        />
      )}
      {isEmailTemplateModalOpen && (
        <EmailTemplateModal
          open={isEmailTemplateModalOpen}
          onClose={() => {
            printStoreEvents.PrintTemplates_TemplateEditor_EmailModal_clicked_close()
            setEmailTemplateModalClose()
          }}
          batchId={getIdFromEncodedString(selectedFlowcode?.id ?? '')}
          flowcodeImageUrl={selectedFlowcode ? getFlowcodeGeneratorURL(selectedFlowcode) : ''}
          onSubmit={onEmailClick}
        />
      )}
    </>
  )
}
